<template>
  <div class="timeline">
    <div class="tabs is-boxed">
      <ul>
        <router-link v-slot="{ href, isActive, navigate }" to="/app/timelines/home">
          <li :class="{ 'is-active': isActive }">
            <a :href="href" @click="navigate">Home</a>
          </li>
        </router-link>
        <router-link v-slot="{ href, isActive, navigate }" to="/app/timelines/you">
          <li :class="{ 'is-active': isActive }">
            <a :href="href" @click="navigate">You</a>
          </li>
        </router-link>
        <router-link v-slot="{ href, isActive, navigate }" to="/app/timelines/mentions">
          <li :class="{ 'is-active': isActive }">
            <a :href="href" @click="navigate">Mentions</a>
          </li>
        </router-link>
        <router-link
            v-for="user in userTimelines"
            :key="user"
            v-slot="{ href, isActive, navigate }"
            :to="'/app/timelines/@' + user">
          <li :class="{ 'is-active': isActive }">
            <a :href="href" @click="navigate">
              <span>@{{ user }}</span>
              <span @click.prevent="deleteTimeline(user)">
                <b-icon class="mr-0" pack="fa" size="is-small" icon="bookmark"/>
              </span>
            </a>
          </li>
        </router-link>
        <li v-if="timelineIsTemporary" class="is-active">
          <a>
            <span>@{{ screenName }}</span>
            <span @click.prevent="$store.commit('addTimeline', screenName)">
              <b-icon class="mr-0" pack="far" size="is-small" icon="bookmark"></b-icon>
            </span>
          </a>
        </li>
        <tabs-add @click="showAddModal = true"/>
      </ul>
    </div>
    <div class="columns">
      <div class="column is-three-fifths">
        <div v-if="data.length" class="content">
          <Tweet
              v-for="(tweet, index) in data"
              :data="tweet"
              :key="tweet.id + '-' + index"
              :show-tweet-actions="true"
              :show-twitter-logo="index === 0"
          ></Tweet>

          <div class="block mt-4">
            <b-button
                @click="getOlder"
                type="is-primary"
                icon-left="angle-double-down"
                outlined expanded
                :disabled="isLoadingOld">Load more
            </b-button>
          </div>
        </div>
      </div>
      <div class="column">
        <div class="notification">
          <p class="block">
            <span v-if="timeline === 'home'">
              The <strong>Home</strong> timeline is Twitter's default view &mdash; it shows
              all the tweets, retweets and likes from people you follow.
            </span>
            <span v-else-if="timeline === 'you'">
              The <strong>you</strong> timeline shows all tweets, replies and retweets made by you. If you want to
              see just your recent tweets, check out <router-link to="/app/recent" class="has-text-link"><strong>Recent</strong>.</router-link>
            </span>
            <span v-else-if="timeline === 'mentions'">
              The <strong>mentions</strong> timeline are all tweets that either @-mention you, or reply to a thread you are in.
            </span>
            <span v-else-if="timeline === 'user'">
              <strong>User</strong> timelines show tweets, retweets and likes made by a specific twitter user.
            </span>
          </p>
          <div class="block">
            <b-button
                @click="getLatest"
                type="is-primary"
                icon-left="sync"
                outlined expanded
                :disabled="isLoadingNew">Load new
            </b-button>
          </div>
        </div>
        <user-profile v-if="profile" :user="profile"/>
      </div>
    </div>
    <b-modal v-model="showAddModal" has-modal-card trap-focus aria-role="dialog" class="dialog dialog-wide has-overflow">
      <template><add-timeline-modal /></template>
    </b-modal>
  </div>

</template>

<script>
import tweets from "../../api/tweets";
import Tweet from "../../components/tweet/Tweet";
import UserProfile from "../../components/UserProfile";
import TabsAdd from "../../components/controls/TabsAdd";
import AddTimelineModal from "../../components/modals/AddTimelineModal";

export default {
  name: "Timeline",
  components: {AddTimelineModal, TabsAdd, UserProfile, Tweet},
  data() {
    return {
      data: [],
      params: {},
      profile: null,
      isLoadingNew: false,
      isLoadingOld: false,
      showAddModal: false,
    }
  },
  computed: {
    userTimelines() {
      return this.$store.state.app.timelines
    },
    activeAccountIndex() {
      return this.$store.state.auth.activeAccountIndex
    },
    timeline() {
      // home / you / mentions / user
      return this.$route.meta.api
    },
    screenName() {
      return this.$route.params.screenName && this.$route.params.screenName.slice(1)
    },
    timelineIsTemporary() {
      return this.timeline === 'user' && this.userTimelines.indexOf(this.screenName) === -1
    }
  },
  created() {
    this.getData()
  },
  watch: {
    '$route': 'getData',
    activeAccountIndex: 'getData',  // reload on profile switch or account login
  },
  methods: {
    getData() {
      this.data = []
      this.profile = null
      if (!this.$store.getters.isLoggedIn) return // wait for auth to process before doing anything

      // get profile
      this.params = {}
      if (this.timeline === 'user') {
        this.params.screen_name = this.screenName
        tweets.getUser(this.screenName).then(response => this.profile = response.data)
      } else if (this.timeline === 'you') {
        tweets.getUser(this.$store.state.auth.profile.screen_name).then(response => this.profile = response.data)
      }
      tweets.timeline(this.timeline, this.params).then(response => {
        this.data = response.data
      })
    },

    getLatest() {
      // we make the same call - we dont do anything clever with the API call -
      // but we then check and insert only what's new
      if (this.data) {
        let existingIds = this.data.map(tweet => tweet.id)
        this.isLoadingNew = true
        tweets.timeline(this.timeline, this.params).then(response => {
          response.data.reverse().forEach(tweet => {
            if (existingIds.indexOf(tweet.id) === -1) {
              this.data.unshift(tweet) // add to front of array
            }
          })
          this.isLoadingNew = false
        })
      }
    },

    getOlder() {
      if (this.data) {
        this.isLoadingOld = true
        let last_tweet = this.data.slice(-1)[0]
        let page_params = {
          max_id: last_tweet.retweeted_id || last_tweet.id,
          ...this.params
        }
        tweets.timeline(this.timeline, page_params).then(response => {
          this.data.push(...response.data)
        })
        this.isLoadingOld = false
      }
    },


    deleteTimeline(user) {
      this.$store.commit('removeTimeline', user)
      if (user === this.screenName) {
        if (this.userTimelines.length) {
          this.$router.push(`/app/timelines/@${this.userTimelines[0]}`)
        } else {
          this.$router.push(`/app/timelines/home`)
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
